<template>
  <div>
    <x-toolbar
      :title="pageTitle"
      :tabs="hasTabs"
      :full-width="true"
      :extension="hasTabs"
    >
      <template
        v-if="hasTabs"
        #extension
      >
        <v-tabs
          v-model="tab"
          background-color="primary lighten-1"
          grow
        >
          <v-tabs-slider
            color="accent"
          />
          <v-tab
            v-for="item of tabItems"
            :key="item.label"
          >
            {{ $t('tasks.stockTaking.navigation.' + item.label) }}
          </v-tab>
        </v-tabs>
      </template>
    </x-toolbar>
    <TaskPrintReport
      v-if="isClosed"
      :api="API"
      :allow-csv="true"
      :csv-handler="csvExport"
    />
    <template v-if="details">
      <template v-if="assignable">
        <TaskAssignButton
          :loading="loading"
          @assign="assign"
        />
      </template>
      <template v-if="hasTabs">
        <v-tabs-items v-model="tab">
          <v-tab-item
            v-for="item of tabItems"
            :key="item.label"
          >
            <component
              :is="item.component"
              :api="API"
              :task-info="taskInfo"
            />
          </v-tab-item>
        </v-tabs-items>
      </template>
      <template v-else>
        <StockTakingAssignment
          :task-info="taskInfo"
        />
      </template>
    </template>
    <template v-else>
      <v-progress-linear
        indeterminate
      />
    </template>
  </div>
</template>

<script>
    import {ProductAPI} from "@/api/ProductAPI";
    import {StockAPI} from "@/api/StockAPI";
    import {TaskStockTakingAPI as API} from "@/api/TaskStockTakingAPI";
    import {RouteParamsMapperMixin} from "@/app/mixins/RouteParamsMapperMixin";
    import TaskAssignButton from "@/app/tasks/components/TaskAssignButton.component";
    import StockTakingAssignment from "@/app/tasks/stockTaking/components/StockTakingAssignment.component";
    import StockTakingTaking from "@/app/tasks/stockTaking/components/StockTakingTaking.component";
    import TaskDefaultOverview from "@/app/tasks/components/TaskDefaultOverview.component";
    import {taskNames, taskTypes} from "@/enum/task_type";
    import {assign} from "@/utils/object";
    import {tabTitle} from "@/utils/string";
    import {ACLMixin} from "@/app/mixins/ACLMixin";
    import {TaskStateMixin} from "@/app/mixins/TaskStateMixin";
    import {TaskAssignMixin} from "@/app/mixins/TaskAssignMixin";
    import {BarcodeListenerMixin} from "@/app/mixins/BarcodeListenerMixin";
    import {EventsListenerMixin} from "@/app/mixins/EventsListenerMixin";
    import * as Export from "@/service/Export";
    import TaskPrintReport from "@/app/tasks/components/TaskPrintReport.component";

    export default {
        name: "StockTakingShow",
        components: {TaskPrintReport, TaskAssignButton, StockTakingAssignment},
        mixins: [ACLMixin, TaskStateMixin, TaskAssignMixin, EventsListenerMixin, BarcodeListenerMixin, RouteParamsMapperMixin],
        data: () => ({
            API: API,
            loading: false,
            loadingReport: false,
            type: taskTypes.STOCK_TAKING,
            details: null,
            tab: null
        }),
        computed: {
            events: function () {
                return {
                    'onBarcodeRead': this.activateTab,
                };
            },
            typeName: function () {
                return this.$t(taskNames[this.type]);
            },
            pageTitle: function () {
                const title = '#' + this.taskId + ' ' + this.typeName;
                if (!this._inactive) {
                    document.title = tabTitle(title);
                }
                return title;
            },
            taskInfo: function () {
                return {
                    details: this.details,
                    tab: this.tab,
                    taskId: this.taskId
                };
            },
            tabItems: function () {
                return [{
                    label: 'assignment',
                    component: StockTakingAssignment
                }]
                    .concat(
                        (
                            this.isStorekeeper || this.isChief
                        ) ? [{
                            label: 'stockTaking',
                            component: StockTakingTaking
                        }] : [])
                    .concat(
                        (
                            this.isOpenState && (
                                (this.isStorekeeper && this.assignedToCurrentUser && this.isInProgress) || this.isChief
                            )
                        ) ? [{
                            label: 'finish',
                            component: TaskDefaultOverview
                        }] : []);
            },
            hasTabs: function () {
                return this.tabItems.length > 1;
            }
        },
        createdOrActivated: function (lifeCycleHook) {
            this.getTask(lifeCycleHook);
        },
        methods: {
            getTask: function (lifeCycleHook) {
                return API.get(this.taskId, true)
                    .then(response => {
                        if (lifeCycleHook === this.LifeCycleHook.CREATED) {
                            this.details = response.data;
                        } else {
                            assign(this.details, response.data);
                        }
                    }).catch(err => {
                        this.snack(err);
                        this.$router.push('/');
                    });
            },
            csvExport: function () { // TODO do this also for stock-loading and stock-picking
                const taskId = this.taskInfo.details.id;
                API.getAllItems(taskId)
                    .then(response => {
                        const promises = [];
                        const exportData = response.data.map(record => {
                            record.stock_location = {};
                            promises.push(StockAPI.getLocationWOStock(record.stock_location_id)
                                .then(response => {
                                    delete response.data.product_locks;
                                    record.stock_location = response.data;
                                }));
                            record.product = {};
                            promises.push(ProductAPI.getInstanceWOProduct(record.product_instance_id)
                                .then(response => {
                                    delete response.data.product.manufacturer;
                                    delete response.data.product.type;
                                    delete response.data.product.attributes;
                                    delete response.data.product.images;
                                    record.product = response.data;
                                }));
                            return record;
                        });
                        Promise.all(promises).then(() => {
                            Export.csv(exportData, [], 'stock_taking_' + taskId + '.csv');
                        }).finally(() => {
                            this.loadingReport = false;
                        });
                    }).catch(this.snack);
            }
        }
    };
</script>

<style scoped>

</style>
